import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IDestinatarioAsistencia, IPopulate, IQueryParam } from 'modelos/src';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../../auxiliares/tabla/filtro/filtro.component';
import { DialogService } from '../../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { DestinatariosAsistenciaService } from '../../../../../../auxiliares/servicios/http/destinatarios-asistencia.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import {
  esc,
  htmlContacto,
  htmlEntidad,
  nombrePaciente,
  resumenPaciente,
  SEXOS,
  sinDato,
} from '../../presentacion-entidades';
import {
  CrearEditarDestinatarioAsistenciaComponent,
  ICrearEditarPacienteData,
} from '../crear-editar-destinatario-asistencia/crear-editar-destinatario-asistencia.component';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';

// La ruta conserva el parámetro `:tipoEmergencia` (hay links externos a
// `destinatariosAsistencia/medica`), pero sólo hay pacientes médicos.
const RUTA = 'main/emergencias/destinatariosAsistencia/medica';

@Component({
  selector: 'app-listado-destinatario-asistencia',
  imports: [AuxiliaresModule],
  templateUrl: './listado-destinatario-asistencia.component.html',
})
export class DestinatarioAsistenciaComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  private service = inject(DestinatariosAsistenciaService);
  private router = inject(Router);
  private formularios = inject(FormularioDialogService);
  private clienteJerarquia = inject(ClienteJerarquiaService);

  public name = 'DestinatarioAsistenciaComponent';
  public datos?: IDestinatarioAsistencia[];
  public totalCount = 0;
  public columnas?: IColumnas<IDestinatarioAsistencia>[];
  private datos$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Emergencias Médicas - Crear pacientes'),
    tooltip: 'Crear paciente',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: ['nombre', 'apellido', 'dni', 'telefono', 'obraSocial'],
  };

  private cliente: IFiltroTabla = {
    filter: { field: 'idCliente' },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // El cliente elegido y toda su descendencia.
  private ancestro: IFiltroTabla = {
    filter: { field: 'idsAncestros', incluirPropio: true },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  private sexo: IFiltroTabla = {
    elementos: SEXOS,
    filter: { field: 'sexo' },
    label: 'Sexo',
    tipo: 'select',
    selectLabel: 'label',
    selectValue: 'value',
  };
  public filtros: IFiltroTabla[] = [this.sexo];

  private populate: IPopulate[] = [{ path: 'cliente' }];
  public query: IQueryParam = {
    filter: JSON.stringify({}),
    populate: JSON.stringify(this.populate),
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public listar(): void {
    // El filtro de la tabla pisa `query.filter`: el tipo se agrega siempre.
    const filtro = this.query.filter ? JSON.parse(this.query.filter) : {};
    const query: IQueryParam = {
      ...this.query,
      filter: JSON.stringify({
        $and: [filtro, { tipoEmergencia: 'Médica' }],
      }),
    };
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2
      .destinatarioAsistencias(query, 30)
      .subscribe((data) => {
        if (HelperService.cambiaronLosDatos(this.datos, data.datos)) {
          this.datos = data.datos;
        }
        this.totalCount = data.totalCount;
      });
  }

  // Acciones

  public crear(): void {
    this.abrirFormulario();
  }

  public editar(p: IDestinatarioAsistencia): void {
    this.abrirFormulario({ idDestinatario: p._id });
  }

  /** Crear/editar en diálogo; al guardar se refresca el listado. */
  private abrirFormulario(data?: ICrearEditarPacienteData): void {
    const ref = this.formularios.abrir(
      CrearEditarDestinatarioAsistenciaComponent,
      { tamano: 'mediano', data },
    );
    ref.afterClosed().subscribe((guardado) => {
      if (guardado) this.listar();
    });
  }

  public detalles(p: IDestinatarioAsistencia): void {
    this.router.navigate([RUTA, 'detalles', p._id]);
  }

  public async eliminar(p: IDestinatarioAsistencia): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Eliminar paciente',
      `¿Confirmás eliminar al paciente <strong>${esc(nombrePaciente(p))}</strong>?`,
    );
    if (!ok) return;
    try {
      await this.service.delete(p._id!);
      this.helper.notifSuccess('Paciente eliminado');
      this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas(): void {
    const columnas: IColumnas<IDestinatarioAsistencia>[] = [
      {
        header: { label: 'Paciente', sort: 'apellido', sortable: true },
        row: {
          html: (d) =>
            htmlEntidad('personal_injury', nombrePaciente(d), [
              resumenPaciente(d),
            ]),
        },
      },
      {
        header: { label: 'Obra social', sort: 'obraSocial', sortable: true },
        row: {
          html: (d) =>
            d.obraSocial ? esc(d.obraSocial) : sinDato('Sin obra social'),
        },
      },
      {
        header: { label: 'Contacto' },
        row: {
          html: (d) => htmlContacto(d.telefono, d.email, d.telefonoAlternativo),
        },
      },
      {
        header: {
          label: 'Fecha de alta',
          sort: 'fechaCreacion',
          sortable: true,
        },
        row: { fecha: (d) => d.fechaCreacion, noData: 'Sin fecha' },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente', hidden: true },
        row: {
          html: (d) => this.clienteJerarquia.htmlNombre(d.cliente),
          tooltip: (d) => this.clienteJerarquia.tooltipJerarquia(d.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        // Menú: Ver detalle primero, Eliminar al final
        accionesGrouped: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (d) => this.detalles(d),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (d) => this.editar(d),
            disabled: () =>
              !this.helper.tienePermiso(
                'Emergencias Médicas - Editar pacientes',
              ),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (d) => this.eliminar(d),
            disabled: () =>
              !this.helper.tienePermiso(
                'Emergencias Médicas - Eliminar pacientes',
              ),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  ngOnInit(): void {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.unshift(this.cliente, this.ancestro);
    }
    this.setColumnas();
    this.listar();
  }

  ngOnDestroy(): void {
    this.datos$?.unsubscribe();
  }
}
